import { useCallback, useEffect, useState } from 'react';
import { dataHooks } from '../../../utils/datahooks';
import { useCollapseItems } from '../../../utils/use-on-rule-group-toggle';
import { useUnsavedChangesHandlers } from '../../../utils/use-unsaved-changes-handlers';
export const useInputFixer = ruleResult => {
  var _ruleResult$fixerProp, _ruleResult$fixerProp2, _ruleResult$fixerProp3, _ruleResult$fixerProp4;
  const {
    onChange,
    onCancel,
    onEdit,
    validator,
    initialValue,
    placeholder,
    onApply,
    onBlur,
    maxInputLength,
    isLargeInput,
    rows,
    suffix,
    autoFocus,
    showInputStatus = true,
    value: controlledValue,
    inputSuffix,
    inputPrefix,
    isEditMode: initialIsEditMode
  } = ruleResult.fixerProps;
  const {
    tips,
    description
  } = ruleResult;
  const unsavedChangesHandlers = useUnsavedChangesHandlers();
  const {
    collapseRule
  } = useCollapseItems();
  const isControlled = controlledValue !== undefined;
  const [uncontrolledValue, setUncontrolledValue] = useState(initialValue ?? '');
  const [isEditMode, setIsEditMode] = useState(initialIsEditMode ?? false);
  const currentValue = isControlled ? controlledValue : uncontrolledValue;
  // TODO: remove uncontrolled state when merging specs.responsive-editor.EnableControlledInputFixer

  const [showLoader, setShowLoader] = useState(false);
  const initialStatusInput = validator == null ? void 0 : validator(currentValue);
  const [inputStatus, setInputStatus] = useState(initialStatusInput);
  useEffect(() => {
    setIsEditMode(initialIsEditMode ?? false);
    return () => {
      setIsEditMode(false);
      setShowLoader(false);
    };
  }, [initialIsEditMode]);
  useEffect(() => {
    const newInputStatus = (validator == null ? void 0 : validator(currentValue)) ?? {
      status: undefined,
      statusMessage: undefined
    };
    setInputStatus({
      status: newInputStatus.status,
      statusMessage: newInputStatus.statusMessage
    });
  }, [validator, currentValue]);
  const onApplyClick = useCallback(async valueToSave => {
    setShowLoader(true);
    try {
      await onApply(valueToSave);
      unsavedChangesHandlers == null || unsavedChangesHandlers.setUnsavedChanges(false, () => Promise.resolve());
    } catch (e) {
      console.error(e);
    } finally {
      setShowLoader(false);
    }
  }, [onApply, unsavedChangesHandlers]);
  const onChangeValue = useCallback(newValue => {
    const validatorResult = validator == null ? void 0 : validator(newValue);
    if (isControlled) {
      onChange == null || onChange(newValue);
    } else {
      setUncontrolledValue(newValue);
      if (newValue === initialValue) {
        unsavedChangesHandlers == null || unsavedChangesHandlers.setUnsavedChanges(false, () => Promise.resolve());
      } else if ((validatorResult == null ? void 0 : validatorResult.status) !== 'error') {
        unsavedChangesHandlers == null || unsavedChangesHandlers.setUnsavedChanges(true, () => onApplyClick(newValue));
      }
      onChange == null || onChange(newValue);
    }
  }, [isControlled, onChange, initialValue, validator, unsavedChangesHandlers, onApplyClick]);
  const onCancelClick = useCallback(() => {
    collapseRule();
    setIsEditMode(false);
    if (!isControlled) {
      setUncontrolledValue(initialValue ?? '');
      onCancel == null || onCancel();
      unsavedChangesHandlers == null || unsavedChangesHandlers.setUnsavedChanges(false, () => Promise.resolve());
    } else {
      onCancel == null || onCancel();
    }
  }, [collapseRule, initialValue, onCancel, unsavedChangesHandlers, isControlled]);
  const onEditClick = useCallback(() => {
    setIsEditMode(true);
    onEdit == null || onEdit();
  }, [setIsEditMode, onEdit]);
  return {
    autoFocus,
    description,
    initialValue: ruleResult.fixerProps.initialValue ?? '',
    inputPrefix,
    inputStatus,
    inputSuffix,
    isEditMode,
    isLargeInput,
    maxInputLength,
    onBlur: () => onBlur == null ? void 0 : onBlur(currentValue),
    onChange: onChangeValue,
    onEditClick,
    placeholder,
    rows,
    ruleFooterProps: {
      dataHook: dataHooks.footerInputFixer(ruleResult.dataHook),
      dismissButtonProps: {
        label: (_ruleResult$fixerProp = ruleResult.fixerProps.footerLabels) == null ? void 0 : _ruleResult$fixerProp.dismiss,
        onClick: onCancelClick,
        type: ((_ruleResult$fixerProp2 = ruleResult.fixerProps.footerLabels) == null || (_ruleResult$fixerProp2 = _ruleResult$fixerProp2.buttonTypes) == null ? void 0 : _ruleResult$fixerProp2.dismiss) ?? 'textButton'
      },
      iconButton: (_ruleResult$fixerProp3 = ruleResult.fixerProps.footerLabels) == null ? void 0 : _ruleResult$fixerProp3.iconButton,
      submitButtonProps: {
        disabled: initialValue === currentValue || (inputStatus == null ? void 0 : inputStatus.status) === 'error' || maxInputLength !== undefined && currentValue.length > maxInputLength,
        label: (_ruleResult$fixerProp4 = ruleResult.fixerProps.footerLabels) == null ? void 0 : _ruleResult$fixerProp4.submit,
        onClick: () => onApplyClick(currentValue),
        showLoader
      }
    },
    showInputStatus,
    suffix,
    tips,
    value: currentValue
  };
};
//# sourceMappingURL=use-input-fixer.js.map